import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Save } from "lucide-react";

export default function InterruptDialog({ open, onOpenChange, onSubmit, currentTask }) {
  const [nextStep, setNextStep] = useState("");
  const [note, setNote] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setNextStep("");
      setNote("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!nextStep.trim()) return;
    onSubmit({ next_step: nextStep.trim(), note: note.trim() || undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--surface))] border-[hsl(var(--pink))]/40 text-white max-w-lg rounded-3xl p-0 overflow-hidden"
        data-testid="interrupt-dialog"
      >
        <div className="h-1 bg-[hsl(var(--pink))]" />
        <div className="p-7">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-[hsl(var(--pink))]/15 border border-[hsl(var(--pink))]/40 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[hsl(var(--pink))]" />
              </div>
              <div>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">
                  Interrupted!
                </DialogTitle>
                <DialogDescription className="text-white/50 text-sm">
                  Lock your next step before your brain wanders.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {currentTask && (
            <div className="mb-4 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
              Pausing: <span className="text-white/70">{currentTask}</span>
            </div>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/50 block mb-2">
                When you come back, your next move is…
              </label>
              <Input
                ref={inputRef}
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                placeholder="e.g. Finish paragraph 3 about pricing"
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl"
                data-testid="interrupt-next-step-input"
                required
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/50 block mb-2">
                Quick context (optional)
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Half-thoughts, links, where to look…"
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl resize-none"
                data-testid="interrupt-note-input"
              />
            </div>

            <Button
              type="submit"
              disabled={!nextStep.trim()}
              className="h-12 rounded-xl bg-[hsl(var(--pink))] hover:bg-[hsl(var(--pink-hi))] text-white font-display font-black uppercase tracking-widest disabled:opacity-40"
              data-testid="interrupt-save-button"
            >
              <Save className="w-4 h-4 mr-2" /> Save & pause
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
